'use strict'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  CircularProgress,
  Select,
  MenuItem,
  Tooltip,
  Collapse,
  IconButton
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CloseIcon from '@material-ui/icons/Close'
import MapIcon from '@material-ui/icons/Map'
import { DFButton } from '../../df'
import { getCrewColor } from './crewColors'
import { getFrequencyLabel } from './scheduleUtils'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider
} from '../../HerbicideSchedule/v3/maricopaBasemap'

const ALL_SECTORS = ['E', 'NE', 'N', 'NW', 'W', 'SW', 'S', 'SE']

const REASON_LABELS = {
  weekly: { label: 'Weekly', tooltip: 'Serviced 48+ times per year (+5 pts)' },
  isolated: { label: 'Isolated', tooltip: 'No other weekly site within 3 miles (+3 pts)' },
  vip: { label: 'VIP', tooltip: 'VIP / priority client (+5 pts)' },
  profitable: { label: 'Profitable', tooltip: 'High margin property (+3 pts)' },
  unprofitable: { label: 'Unprofitable', tooltip: 'Operating at a loss (legacy signal, no current score boost)' },
  underestimated: { label: 'Underestimated', tooltip: 'Time consistently underestimated (+3 pts)' },
  manual_selected: { label: 'Manual', tooltip: 'Manually promoted by admin' }
}

function getReasonLabel (code) {
  if (REASON_LABELS[code]) return REASON_LABELS[code]
  if (/h_onsite$/.test(code)) {
    return { label: 'Long service', tooltip: `${code.replace('_onsite', '').replace('>=', '')} hours per visit (+4 pts)` }
  }
  if (/mi_from_yard$/.test(code)) {
    return { label: 'Far from yard', tooltip: `${code.replace('_from_yard', '').replace('>=', '')} miles from yard (+3 pts)` }
  }
  return { label: code, tooltip: code }
}

function isWeeklyAnchorEligible (item = {}) {
  if (typeof item.weeklyAnchorEligible === 'boolean') return item.weeklyAnchorEligible
  const code = String(item.frequencyCode || '').trim().toUpperCase()
  return code === 'W'
}

function isSuggestedAnchorViewItem (item = {}) {
  if (typeof item.suggestedAnchor === 'boolean') return item.suggestedAnchor
  return item.isAnchor === true || (isWeeklyAnchorEligible(item) && item.isAnchorCandidate)
}

function getCandidateMarkerColor (score) {
  const n = Number(score || 0)
  if (n >= 18) return '#1b5e20'
  if (n >= 15) return '#2e7d32'
  if (n >= 12) return '#7cb342'
  if (n >= 10) return '#f9a825'
  if (n >= 7) return '#ef6c00'
  return '#9e9e9e'
}

/* ── Score badge colors (DF prototype tiers) ──────────────────────── */
function scoreColor (s) {
  if (s >= 18) return '#164a2c'
  if (s >= 12) return '#3454c1'
  if (s >= 8) return '#a4502f'
  return '#6f7a6b'
}
function scoreBg (s) {
  if (s >= 18) return '#e3eee0'
  if (s >= 12) return '#e6ebfa'
  if (s >= 8) return '#fbe8d6'
  return '#f6f1e3'
}

const WORKFLOW_STEPS = [
  { num: 1, label: 'Score', desc: 'Analyze props' },
  { num: 2, label: 'Promote', desc: 'Pick anchors' },
  { num: 3, label: 'Assign Crews', desc: 'Link to routes' },
  { num: 4, label: 'Centroids', desc: 'Optimize' }
]
const ANCHOR_VIEW_MODES = {
  suggested: 'suggested',
  weekly: 'weekly',
  all: 'all'
}

const DEFAULT_CENTER = { lat: 33.45, lng: -112.07 }
const DEFAULT_ZOOM = 10

export default function AnchorsTab ({
  url,
  selectedScenarioId,
  crews,
  crewIds,
  onSnackbar,
  onRefreshMetrics
}) {
  const [anchorCandidates, setAnchorCandidates] = useState([])
  const [anchorCoverage, setAnchorCoverage] = useState(null)
  const [anchorViewCounts, setAnchorViewCounts] = useState({ suggestedAnchors: 0, allWeeklyProperties: 0, allProperties: 0 })
  const [loadingAnchors, setLoadingAnchors] = useState(false)
  const [runningAnchorScoring, setRunningAnchorScoring] = useState(false)
  const [runningCentroids, setRunningCentroids] = useState(false)
  const [savingAnchorByPropertyId, setSavingAnchorByPropertyId] = useState({})
  const [anchorCrewChoiceByPropertyId, setAnchorCrewChoiceByPropertyId] = useState({})
  const [mapOpen, setMapOpen] = useState(true)
  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [anchorViewMode, setAnchorViewMode] = useState(ANCHOR_VIEW_MODES.suggested)

  // Leaflet map refs
  const mapContainerRef = useRef(null)
  const mapRef = useRef(null)
  const dataLayerRef = useRef(null)
  const baseTileLayerRef = useRef(null)
  const maricopaTileLayerRef = useRef(null)

  const fetchAnchorCandidates = useCallback(async (scenarioId) => {
    setLoadingAnchors(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/anchors/candidates`, {
        params: { scenarioId: scenarioId || undefined, candidateOnly: false, limit: 5000 }
      })
      const nextRows = response.data?.rows || []
      setAnchorCandidates(nextRows)
      setAnchorCoverage(response.data?.coverage || null)
      setAnchorViewCounts(response.data?.viewCounts || {
        suggestedAnchors: nextRows.filter(item => isSuggestedAnchorViewItem(item)).length,
        allWeeklyProperties: nextRows.filter(item => isWeeklyAnchorEligible(item)).length,
        allProperties: nextRows.length
      })
      setAnchorCrewChoiceByPropertyId(nextRows.reduce((acc, item) => {
        if (item.isAnchor) {
          acc[item.aspirePropertyId] = item.anchorCrewId || item.suggestedCrewId || ''
        }
        return acc
      }, {}))
    } catch (err) {
      console.error('Error loading anchor candidates:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to load anchor candidates', 'error')
      setAnchorCandidates([])
      setAnchorCoverage(null)
      setAnchorViewCounts({ suggestedAnchors: 0, allWeeklyProperties: 0, allProperties: 0 })
    } finally {
      setLoadingAnchors(false)
    }
  }, [url, onSnackbar])

  useEffect(() => {
    if (!selectedScenarioId) {
      setAnchorCandidates([])
      setAnchorCoverage(null)
      setAnchorViewCounts({ suggestedAnchors: 0, allWeeklyProperties: 0, allProperties: 0 })
      setAnchorCrewChoiceByPropertyId({})
      return
    }
    fetchAnchorCandidates(selectedScenarioId)
  }, [selectedScenarioId, fetchAnchorCandidates])

  const runAnchorScoring = async () => {
    setRunningAnchorScoring(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/compute-anchors`, {}, {
        params: { scenarioId: selectedScenarioId || undefined }
      })
      const scored = response.data?.scored || 0
      const candidateCount = response.data?.candidates || 0
      onSnackbar(`Anchor scoring complete: ${candidateCount} candidates across ${scored} properties`, 'success')
      await fetchAnchorCandidates(selectedScenarioId)
    } catch (err) {
      console.error('Error computing anchors:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to compute anchors', 'error')
    } finally {
      setRunningAnchorScoring(false)
    }
  }

  const runCentroidCompute = async () => {
    setRunningCentroids(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/compute-centroids`, {})
      const updatedCount = response.data?.updatedCount || 0
      onSnackbar(`Crew centroids updated (${updatedCount} crews)`, 'success')
      if (onRefreshMetrics) onRefreshMetrics()
    } catch (err) {
      console.error('Error computing centroids:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to compute centroids', 'error')
    } finally {
      setRunningCentroids(false)
    }
  }

  const saveAnchor = async (candidate, isAnchor, crewId) => {
    const propertyId = candidate?.aspirePropertyId
    if (!propertyId) return
    if (isAnchor && !isWeeklyAnchorEligible(candidate)) {
      onSnackbar('Only weekly properties can be anchors', 'warning')
      return
    }
    setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: true }))
    try {
      const body = { isAnchor: !!isAnchor }
      if (crewId) body.anchorCrewId = crewId
      const response = await axios.patch(`${url}/api/hmi/territory/anchors/${propertyId}`, body)
      const updatedCoverage = response.data?.coverage || null
      if (updatedCoverage) setAnchorCoverage(updatedCoverage)
      onSnackbar(isAnchor ? 'Anchor promoted' : 'Anchor removed', 'success')
      await fetchAnchorCandidates(selectedScenarioId)
    } catch (err) {
      console.error('Error saving anchor:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to save anchor', 'error')
    } finally {
      setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: false }))
    }
  }

  const saveCrewAssignment = async (item) => {
    const propertyId = item?.aspirePropertyId
    if (!propertyId) return
    const crewId = anchorCrewChoiceByPropertyId[propertyId] || ''
    if (!crewId) {
      onSnackbar('Select a crew first', 'warning')
      return
    }
    setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: true }))
    try {
      const response = await axios.patch(`${url}/api/hmi/territory/anchors/${propertyId}`, {
        isAnchor: true,
        anchorCrewId: crewId
      })
      const updatedCoverage = response.data?.coverage || null
      if (updatedCoverage) setAnchorCoverage(updatedCoverage)
      onSnackbar('Crew assigned', 'success')
      await fetchAnchorCandidates(selectedScenarioId)
    } catch (err) {
      console.error('Error assigning crew:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to assign crew', 'error')
    } finally {
      setSavingAnchorByPropertyId(prev => ({ ...prev, [propertyId]: false }))
    }
  }

  const visibleRows = useMemo(() => {
    if (anchorViewMode === ANCHOR_VIEW_MODES.suggested) {
      return anchorCandidates.filter(isSuggestedAnchorViewItem)
    }
    if (anchorViewMode === ANCHOR_VIEW_MODES.weekly) {
      return anchorCandidates.filter(item => isWeeklyAnchorEligible(item))
    }
    return anchorCandidates
  }, [anchorCandidates, anchorViewMode])

  const allActiveAnchors = useMemo(() => anchorCandidates.filter(item => item.isAnchor), [anchorCandidates])
  const activeAnchors = useMemo(() => visibleRows.filter(item => item.isAnchor), [visibleRows])
  const candidates = useMemo(() => visibleRows.filter(item => !item.isAnchor), [visibleRows])

  const currentStep = useMemo(() => {
    if (allActiveAnchors.length > 0 && allActiveAnchors.every(a => a.anchorCrewId)) return 3
    if (allActiveAnchors.length > 0) return 2
    if (anchorCandidates.length > 0) return 1
    return 0
  }, [allActiveAnchors, anchorCandidates.length])

  const coverageSummaryText = useMemo(() => {
    if (!anchorCoverage) return null
    const count = anchorCoverage.anchorCount || 0
    const covered = (anchorCoverage.coveredSectors || []).length
    const missing = (anchorCoverage.missingSectors || []).length
    if (missing === 0) return `${count} anchor${count !== 1 ? 's' : ''} across all 8 sectors - full coverage`
    return `${count} anchor${count !== 1 ? 's' : ''} across ${covered} of 8 sectors - ${missing} gap${missing !== 1 ? 's' : ''} remaining`
  }, [anchorCoverage])

  const mappableItems = useMemo(() =>
    visibleRows.filter(item =>
      Number.isFinite(item.latitude) && Number.isFinite(item.longitude)
    ), [visibleRows])

  const yardPosition = useMemo(() => {
    if (!anchorCoverage?.yard) return null
    const { latitude, longitude } = anchorCoverage.yard
    return Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { lat: latitude, lng: longitude } : null
  }, [anchorCoverage])

  /* ── Leaflet map init ───────────────────────────────────────────── */
  const handleMapContainerRef = useCallback((node) => {
    mapContainerRef.current = node
  }, [])

  useEffect(() => {
    const node = mapContainerRef.current
    if (!node || mapRef.current || !mapOpen) return

    const map = L.map(node, {
      center: [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
      zoom: DEFAULT_ZOOM,
      maxZoom: 28,
      zoomControl: true,
      attributionControl: false
    })

    applyBasemapProvider({
      map,
      tileLayerRef: baseTileLayerRef,
      provider: googleHybridProvider
    })
    if (baseTileLayerRef.current?.setZIndex) baseTileLayerRef.current.setZIndex(0)

    mapRef.current = map

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapRef.current) return
        const maricopaLayer = applyBasemapProvider({
          map,
          tileLayerRef: maricopaTileLayerRef,
          provider
        })
        if (maricopaLayer?.setZIndex) maricopaLayer.setZIndex(1)
      })
      .catch((err) => {
        console.warn('Failed to load Maricopa basemap for anchors:', err)
      })

    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
    }, 150)

    return () => {
      cancelled = true
      if (dataLayerRef.current) {
        try { map.removeLayer(dataLayerRef.current) } catch (_) {}
        dataLayerRef.current = null
      }
      if (maricopaTileLayerRef.current) {
        try { map.removeLayer(maricopaTileLayerRef.current) } catch (_) {}
        maricopaTileLayerRef.current = null
      }
      if (baseTileLayerRef.current) {
        try { map.removeLayer(baseTileLayerRef.current) } catch (_) {}
        baseTileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapRef.current = null
    }
  }, [mapOpen])

  /* ── Leaflet markers ────────────────────────────────────────────── */
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (dataLayerRef.current) {
      try { map.removeLayer(dataLayerRef.current) } catch (_) {}
    }
    const dataLayer = L.layerGroup().addTo(map)
    dataLayerRef.current = dataLayer

    const bounds = L.latLngBounds([])

    mappableItems.forEach(item => {
      const lat = item.latitude
      const lng = item.longitude
      const isSelected = item.aspirePropertyId === selectedPropertyId
      const isAnchor = item.isAnchor
      const score = Number(item.anchorScore || 0)

      let fillColor = getCandidateMarkerColor(score)
      let radius = isAnchor ? 10 : (score >= 18 ? 8 : score >= 12 ? 7 : 6)
      let borderColor = '#fff'
      let borderWidth = 1.5

      if (isAnchor) {
        const crewId = anchorCrewChoiceByPropertyId[item.aspirePropertyId] || item.anchorCrewId || ''
        fillColor = crewId ? getCrewColor(crewId, crewIds) : '#4caf50'
        borderWidth = 2
      } else if (item.suppressedByNearbyHigher) {
        fillColor = '#9e9e9e'
        borderColor = '#6d4c41'
        borderWidth = 2
      }

      if (isSelected) {
        radius += 4
        borderColor = 'var(--df-primary, #1f5e3a)'
        borderWidth = 3
      }

      const marker = L.circleMarker([lat, lng], {
        radius,
        fillColor,
        fillOpacity: isAnchor ? 0.95 : (item.suppressedByNearbyHigher ? 0.42 : 0.82),
        color: borderColor,
        weight: borderWidth
      }).addTo(dataLayer)

      marker.bindTooltip(item.propertyName, { direction: 'top', offset: [0, -radius] })
      marker.on('click', () => {
        setSelectedPropertyId(prev => prev === item.aspirePropertyId ? null : item.aspirePropertyId)
      })

      bounds.extend([lat, lng])
    })

    // Yard marker
    if (yardPosition) {
      const yardIcon = L.divIcon({
        html: '<div style="width:0;height:0;border-left:7px solid transparent;border-right:7px solid transparent;border-top:14px solid #000;filter:drop-shadow(0 1px 2px rgba(0,0,0,.4))"></div>',
        className: '',
        iconSize: [14, 14],
        iconAnchor: [7, 14]
      })
      L.marker([yardPosition.lat, yardPosition.lng], { icon: yardIcon })
        .bindTooltip('Yard', { direction: 'top', offset: [0, -14] })
        .addTo(dataLayer)
      bounds.extend([yardPosition.lat, yardPosition.lng])
    }

    if (bounds.isValid()) {
      try { map.fitBounds(bounds, { padding: [50, 50] }) } catch (_) {}
    }

    return () => {
      if (dataLayerRef.current) {
        try { map.removeLayer(dataLayerRef.current) } catch (_) {}
        dataLayerRef.current = null
      }
    }
  }, [mappableItems, yardPosition, selectedPropertyId, anchorCrewChoiceByPropertyId, crewIds])

  useEffect(() => {
    if (!selectedPropertyId) return
    if (visibleRows.some(item => item.aspirePropertyId === selectedPropertyId)) return
    setSelectedPropertyId(null)
  }, [selectedPropertyId, visibleRows])

  const isLoading = loadingAnchors || runningAnchorScoring || runningCentroids

  if (!selectedScenarioId) {
    return (
      <div style={{ padding: 20 }}>
        <Alert severity="info">
          Select a scenario above to view and manage anchor properties. Anchors are high-priority properties that serve as geographic reference points for crew territory assignments.
        </Alert>
      </div>
    )
  }

  const coveredSectors = anchorCoverage?.coveredSectors || []
  const suggestedCount = Number(anchorViewCounts?.suggestedAnchors || 0)
  const allWeeklyCount = Number(anchorViewCounts?.allWeeklyProperties || 0)
  const allPropertiesCount = Number(anchorViewCounts?.allProperties || anchorCandidates.length || 0)
  const visibleCandidatesTitle = anchorViewMode === ANCHOR_VIEW_MODES.suggested
    ? 'Suggested Candidates'
    : (anchorViewMode === ANCHOR_VIEW_MODES.weekly ? 'Weekly Properties (non-anchor)' : 'Properties (non-anchor)')

  const selectProperty = (aspirePropertyId) => {
    setSelectedPropertyId(prev => prev === aspirePropertyId ? null : aspirePropertyId)
  }

  const selectedItem = selectedPropertyId
    ? visibleRows.find(c => c.aspirePropertyId === selectedPropertyId)
    : null

  const getScoreColor = (score) => {
    if (score >= 15) return '#2e7d32'
    if (score >= 10) return '#1565c0'
    if (score >= 5) return '#e65100'
    return '#9e9e9e'
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
      {/* ── Sticky header: stepper + actions + coverage ────────────── */}
      <div style={{
        flexShrink: 0, background: 'var(--df-surface)',
        borderBottom: '1px solid var(--df-line)', padding: '12px 20px'
      }}>
        {/* Compact stepper */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
          {WORKFLOW_STEPS.map((step, i) => (
            <span key={step.num}>
              {i > 0 && <div className={'step-connector' + (step.num <= currentStep ? ' done' : '')} />}
              <div className={'step-node' + (step.num === currentStep ? ' active' : '') + (step.num < currentStep ? ' done' : '')}>
                {step.num < currentStep
                  ? <CheckCircleIcon style={{ fontSize: 14 }} />
                  : <span style={{ fontSize: 11, fontWeight: 800 }}>{step.num}</span>
                }
                <div>
                  <div style={{ fontSize: 12, lineHeight: 1 }}>{step.label}</div>
                  <div style={{ fontSize: 10, opacity: 0.65, lineHeight: 1.2, marginTop: 1 }}>{step.desc}</div>
                </div>
              </div>
            </span>
          ))}
        </div>

        {/* Action buttons + sector coverage */}
        <div style={{ display: 'flex', gap: 8, marginTop: 11, alignItems: 'center', flexWrap: 'wrap' }}>
          <DFButton
            size="small"
            variant="outlined"
            disabled={runningAnchorScoring}
            onClick={runAnchorScoring}
          >
            {runningAnchorScoring ? 'Scoring...' : 'Score Candidates'}
          </DFButton>
          <DFButton
            size="small"
            variant="outlined"
            disabled={runningCentroids}
            onClick={runCentroidCompute}
          >
            {runningCentroids ? 'Computing...' : 'Compute Centroids'}
          </DFButton>
          <Tooltip title="Reload anchor data" arrow>
            <span>
              <DFButton
                size="small"
                variant="outlined"
                disabled={loadingAnchors}
                onClick={() => fetchAnchorCandidates(selectedScenarioId)}
              >
                <RefreshIcon style={{ fontSize: 15 }} />
              </DFButton>
            </span>
          </Tooltip>
          {isLoading && <CircularProgress size={18} style={{ color: 'var(--df-primary)' }} />}

          {/* Sector coverage chips */}
          {anchorCoverage && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4)', marginRight: 4 }}>COVERAGE</span>
              {ALL_SECTORS.map(sector => {
                const covered = coveredSectors.includes(sector)
                return (
                  <div
                    key={sector}
                    className="sector-chip"
                    style={{
                      background: covered ? '#e3eee0' : '#f7dccf',
                      color: covered ? '#164a2c' : '#a4502f',
                      borderColor: covered ? '#b6d4be' : '#e8c4a8'
                    }}
                  >
                    {sector}
                  </div>
                )
              })}
            </div>
          )}
        </div>
        {coverageSummaryText && (
          <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 6 }}>{coverageSummaryText}</div>
        )}
      </div>

      {/* ── Main scroll ───────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Map Panel */}
        <div className="rw-card" style={{ overflow: 'hidden' }}>
          <div
            style={{
              padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
              borderBottom: '1px solid var(--df-line)', cursor: 'pointer'
            }}
            onClick={() => setMapOpen(p => !p)}
          >
            <MapIcon style={{ fontSize: 16, color: 'var(--df-primary)' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink)', flex: 1 }}>Anchor Map</span>
            <span className="rw-pill pill-due">{allActiveAnchors.length} active anchors</span>
            <span className="rw-pill pill-freq">{candidates.length} candidates</span>
            <IconButton size="small">
              {mapOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </div>

          <Collapse in={mapOpen}>
            <div
              ref={handleMapContainerRef}
              style={{ width: '100%', height: 400, minHeight: 300, background: '#d8e4d0' }}
            />
            <div style={{
              display: 'flex', gap: 16, padding: '8px 14px', alignItems: 'center',
              borderTop: '1px solid var(--df-line)', flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--df-ink-4)' }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4caf50', display: 'inline-block' }} />
                Active anchor
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--df-ink-4)' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getCandidateMarkerColor(18), display: 'inline-block' }} />
                Candidate 18+
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--df-ink-4)' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getCandidateMarkerColor(14), display: 'inline-block' }} />
                Candidate 14-17
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--df-ink-4)' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: getCandidateMarkerColor(10), display: 'inline-block' }} />
                Candidate 10-13
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--df-ink-4)' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9e9e9e', opacity: 0.42, border: '2px solid #6d4c41', display: 'inline-block' }} />
                Suppressed
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--df-ink-4)' }}>
                <span style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '10px solid #000', display: 'inline-block' }} />
                Yard
              </div>
            </div>
          </Collapse>
        </div>

        {/* View mode buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {[
            { v: ANCHOR_VIEW_MODES.suggested, l: `Suggested Anchors (${suggestedCount})` },
            { v: ANCHOR_VIEW_MODES.weekly, l: `All Weekly (${allWeeklyCount})` },
            { v: ANCHOR_VIEW_MODES.all, l: `All Properties (${allPropertiesCount})` }
          ].map(opt => (
            <DFButton
              key={opt.v}
              size="small"
              variant={anchorViewMode === opt.v ? 'contained' : 'outlined'}
              color={anchorViewMode === opt.v ? 'primary' : 'default'}
              onClick={() => setAnchorViewMode(opt.v)}
            >
              {opt.l}
            </DFButton>
          ))}
          <span className="rw-pill pill-freq" style={{ marginLeft: 4 }}>
            Showing {visibleRows.length}
          </span>
        </div>

        {/* Active Anchors table */}
        {activeAnchors.length > 0 && (
          <div className="rw-card" style={{ overflow: 'hidden' }}>
            <div style={{
              padding: '10px 14px', borderBottom: '1px solid var(--df-line)',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink)' }}>Active Anchors</span>
              <span className="rw-pill pill-due">{activeAnchors.length}</span>
              {activeAnchors.filter(a => !a.anchorCrewId).length > 0 && (
                <span style={{ fontSize: 11, color: '#a9341d', fontWeight: 600 }}>
                  {activeAnchors.filter(a => !a.anchorCrewId).length} need crew
                </span>
              )}
            </div>
            <div className="tbl-wrap" style={{ maxHeight: 300 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th style={{ width: 60 }}>Score</th>
                    <th>Crew</th>
                    <th style={{ width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {activeAnchors.map(item => {
                    const anchorChoice = anchorCrewChoiceByPropertyId[item.aspirePropertyId] || item.anchorCrewId || ''
                    const crewColor = anchorChoice ? getCrewColor(anchorChoice, crewIds) : '#ccc'
                    const isSelected = item.aspirePropertyId === selectedPropertyId
                    return (
                      <tr
                        key={item.aspirePropertyId}
                        className={isSelected ? 'selected' : ''}
                        onClick={() => selectProperty(item.aspirePropertyId)}
                        style={{ cursor: 'pointer', borderLeft: '3px solid var(--df-primary)' }}
                      >
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 12 }}>{item.propertyName}</div>
                          <div style={{ fontSize: 10.5, color: 'var(--df-ink-4)' }}>
                            #{item.aspirePropertyId} | {getFrequencyLabel(item)}
                          </div>
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: '50%', fontSize: 12, fontWeight: 800,
                            background: scoreBg(item.anchorScore), color: scoreColor(item.anchorScore)
                          }}>
                            {Math.round(item.anchorScore || 0)}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{
                              display: 'inline-block', width: 10, height: 10,
                              borderRadius: '50%', backgroundColor: crewColor
                            }} />
                            {anchorChoice
                              ? <span style={{ fontSize: 12 }}>{(crews.find(c => c._id === anchorChoice)?.name || 'Assigned')}</span>
                              : <span style={{ fontSize: 12, color: '#a9341d', fontWeight: 600 }}>Needs crew</span>
                            }
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: 14, color: 'var(--df-ink-4)' }}>&#8250;</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Candidates table */}
        <div className="rw-card" style={{ overflow: 'hidden' }}>
          <div style={{
            padding: '10px 14px', borderBottom: '1px solid var(--df-line)',
            display: 'flex', alignItems: 'center', gap: 8
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink)' }}>{visibleCandidatesTitle}</span>
            <span className="rw-pill pill-freq">{candidates.length}</span>
          </div>
          <div className="tbl-wrap" style={{ maxHeight: 400 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Property</th>
                  <th style={{ width: 60 }}>Score</th>
                  <th>Reasons</th>
                  <th style={{ width: 30 }} />
                </tr>
              </thead>
              <tbody>
                {candidates.slice(0, 30).map(item => {
                  const isSelected = item.aspirePropertyId === selectedPropertyId
                  return (
                    <tr
                      key={item.aspirePropertyId}
                      className={isSelected ? 'selected' : ''}
                      onClick={() => selectProperty(item.aspirePropertyId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{item.propertyName}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--df-ink-4)' }}>
                          #{item.aspirePropertyId} | {getFrequencyLabel(item)}
                          {!isWeeklyAnchorEligible(item) ? ' | Not anchor-eligible' : ''}
                        </div>
                        {item.suppressedByNearbyHigher && (
                          <div style={{ fontSize: 10, color: 'var(--df-ink-4)', marginTop: 2 }}>
                            Suppressed (near #{item.suppressedByAspirePropertyId}, +{item.suppressedByScoreDelta} pts, {item.suppressedByDistanceMiles} mi)
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="score-badge" style={{
                          width: 28, height: 28, fontSize: 12, fontWeight: 800,
                          background: scoreBg(item.anchorScore), color: scoreColor(item.anchorScore)
                        }}>
                          {Math.round(item.anchorScore || 0)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(item.anchorReason || []).length > 0
                            ? (item.anchorReason || []).map((code, idx) => {
                              const reason = getReasonLabel(code)
                              return (
                                <Tooltip key={idx} title={reason.tooltip} arrow>
                                  <span className="rw-pill pill-freq">{reason.label}</span>
                                </Tooltip>
                              )
                            })
                            : <span style={{ fontSize: 11, color: 'var(--df-ink-4)' }}>-</span>
                          }
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: 14, color: 'var(--df-ink-4)' }}>&#8250;</span>
                      </td>
                    </tr>
                  )
                })}
                {!loadingAnchors && visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--df-ink-4)' }}>
                      No properties match this view. Try another filter mode or click "Score Candidates".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Detail drawer ─────────────────────────────────────────── */}
      {selectedItem && <div className="cap-drawer-scrim" onClick={() => setSelectedPropertyId(null)} />}
      {selectedItem && (() => {
        const saving = !!savingAnchorByPropertyId[selectedItem.aspirePropertyId]
        const anchorChoice = anchorCrewChoiceByPropertyId[selectedItem.aspirePropertyId] || selectedItem.anchorCrewId || ''
        const crewColor = anchorChoice ? getCrewColor(anchorChoice, crewIds) : '#ccc'
        const score = Math.round(selectedItem.anchorScore || 0)
        const weeklyEligible = isWeeklyAnchorEligible(selectedItem)
        return (
          <div className="cap-drawer">
            <div style={{
              padding: '14px 16px', borderBottom: '1px solid var(--df-line)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink)', lineHeight: 1.3 }}>
                  {selectedItem.propertyName}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                  <span className="rw-pill" style={{
                    background: selectedItem.isAnchor ? '#e3eee0' : '#e6ebfa',
                    color: selectedItem.isAnchor ? '#164a2c' : '#3454c1'
                  }}>
                    {selectedItem.isAnchor ? 'Active Anchor' : 'Candidate'}
                  </span>
                  <span className="rw-pill pill-freq">#{selectedItem.aspirePropertyId}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedPropertyId(null)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', padding: 4, borderRadius: 4, color: 'var(--df-ink-3)'
                }}
              >
                <CloseIcon style={{ fontSize: 18 }} />
              </button>
            </div>

            <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
              {/* Score badge + reasons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div className="score-badge" style={{ background: getScoreColor(score) }}>{score}</div>
                <div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--df-ink-4)',
                    textTransform: 'uppercase', letterSpacing: '0.05em'
                  }}>Anchor Score</div>
                  {(selectedItem.anchorReason || []).length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--df-ink-3)', marginTop: 2 }}>Why this scored high:</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {(selectedItem.anchorReason || []).map((code, idx) => {
                          const reason = getReasonLabel(code)
                          return (
                            <Tooltip key={idx} title={reason.tooltip} arrow>
                              <span className="rw-pill pill-freq">{reason.label}</span>
                            </Tooltip>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Details */}
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--df-ink-4)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6
                }}>Details</div>
                {[
                  { label: 'Frequency', value: getFrequencyLabel(selectedItem) },
                  { label: 'Annual visits', value: selectedItem.annualOccurrences || 0 },
                  {
                    label: 'Median on-site',
                    value: selectedItem.medianOnSiteHours > 0
                      ? (() => {
                        const wallMin = selectedItem.medianOnSiteMinutes || Math.round(selectedItem.medianOnSiteHours * 60)
                        return `${(wallMin / 60).toFixed(1)} hrs`
                      })()
                      : '—'
                  },
                  { label: 'Coordinates', value: Number.isFinite(selectedItem.latitude) ? 'Yes' : 'No' }
                ].map(row => (
                  <div key={row.label} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12
                  }}>
                    <span style={{ color: 'var(--df-ink-4)' }}>{row.label}</span>
                    <span style={{ fontWeight: 500, color: 'var(--df-ink)' }}>{row.value}</span>
                  </div>
                ))}
              </div>

              {/* Warnings */}
              {!weeklyEligible && (
                <div style={{
                  marginBottom: 16, padding: '8px 12px', borderRadius: 8,
                  background: '#fbe8d6', color: '#a4502f', fontSize: 12
                }}>
                  Anchors are restricted to weekly properties. This property is {getFrequencyLabel(selectedItem)}.
                </div>
              )}

              {selectedItem.suppressedByNearbyHigher && (
                <div style={{
                  marginBottom: 16, padding: '8px 12px', borderRadius: 8,
                  background: '#e6ebfa', color: '#3454c1', fontSize: 12
                }}>
                  Hidden from Suggested Anchors because #{selectedItem.suppressedByAspirePropertyId} is {selectedItem.suppressedByScoreDelta} points higher within {selectedItem.suppressedByDistanceMiles} mile(s).
                </div>
              )}

              {/* Crew assignment for active anchors */}
              {selectedItem.isAnchor && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--df-ink-4)',
                    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6
                  }}>Crew Assignment</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      display: 'inline-block', width: 12, height: 12,
                      borderRadius: '50%', backgroundColor: crewColor
                    }} />
                    <Select
                      value={anchorChoice}
                      onChange={(e) => {
                        setAnchorCrewChoiceByPropertyId(prev => ({
                          ...prev,
                          [selectedItem.aspirePropertyId]: e.target.value
                        }))
                      }}
                      variant="outlined"
                      size="small"
                      disabled={saving}
                      style={{ flex: 1, fontSize: 13 }}
                    >
                      <MenuItem value="" disabled>Select crew</MenuItem>
                      {crews.map(crew => (
                        <MenuItem key={crew._id} value={crew._id}>{crew.name}</MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 6 }}>
                {selectedItem.isAnchor ? (
                  <>
                    <DFButton
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={saving || !anchorChoice || !weeklyEligible}
                      onClick={() => saveCrewAssignment(selectedItem)}
                      style={{ flex: 1 }}
                    >
                      {saving ? 'Saving...' : (weeklyEligible ? 'Save Crew' : 'Weekly Only')}
                    </DFButton>
                    <DFButton
                      variant="outlined"
                      size="small"
                      disabled={saving}
                      onClick={() => saveAnchor(selectedItem, false)}
                      style={{ flex: 1, color: '#a9341d', borderColor: '#a9341d' }}
                    >
                      Demote
                    </DFButton>
                  </>
                ) : (
                  <DFButton
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={saving || !weeklyEligible}
                    onClick={() => saveAnchor(selectedItem, true)}
                    style={{ flex: 1 }}
                  >
                    {saving ? 'Saving...' : (weeklyEligible ? 'Promote to Anchor' : 'Weekly Only')}
                  </DFButton>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
